import { useEffect, useRef, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import type { DemoTask } from "./fixtures/demoTasks";
import { getAxisValueIds } from "./taskBoardAxes";
import { TASK_ACTION_LABELS, type TaskBoardChange } from "./taskBoardActions";
import {
    TaskDueDatePicker,
    TaskEntitiesPicker,
    TaskFieldPicker,
} from "./taskBoardPickers";
import { TaskChipButton } from "@components/tasks/board/TaskChipButton";
import type { TaskShortcutPick } from "./useTaskBoardShortcuts";
import styles from "./BoardPlayground.module.scss";

interface TaskBoardShortcutPickerProps {
    pick: TaskShortcutPick | null;
    /** The tasks on the board, for the current value of a single task. */
    tasks: readonly DemoTask[];
    onApply: (taskIds: ReadonlySet<string>, change: TaskBoardChange) => void;
    onClose: (card: HTMLElement) => void;
}

const getAnchorStyle = ({
    top,
    left,
    width,
    height,
}: DOMRect): CSSProperties => ({
    position: "fixed",
    top,
    left,
    width,
    height,
});

/** The date picker has no open prop: its trigger is clicked once it mounts. */
const DueDateShortcutPicker = ({
    dueDateOnly,
    style,
    onChange,
    onClose,
}: {
    dueDateOnly: string | null;
    style: CSSProperties;
    onChange: (change: TaskBoardChange) => void;
    onClose: () => void;
}) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        triggerRef.current?.click();
    }, []);
    return (
        <TaskDueDatePicker
            dueDateOnly={dueDateOnly}
            onChange={onChange}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onClose();
                }
            }}
            trigger={
                <TaskChipButton
                    ref={triggerRef}
                    aria-label={TASK_ACTION_LABELS.dueDate}
                    className={styles.shortcutAnchor}
                    style={style}
                />
            }
        />
    );
};

/**
 * The picker a card shortcut opened, from the same set the chips and the
 * command bar use. An invisible trigger sits over the anchor (the field's
 * chip, else the card), so the popup opens where a click on the chip
 * would open it. The pick reaches every target task.
 */
export const TaskBoardShortcutPicker = ({
    pick,
    tasks,
    onApply,
    onClose,
}: Readonly<TaskBoardShortcutPickerProps>) => {
    if (pick === null) {
        return null;
    }
    const { field, target } = pick;
    const style = getAnchorStyle(pick.anchorRect);
    const task =
        target.itemIds.size === 1
            ? tasks.find(({ id }) => id === target.itemId)
            : undefined;
    const onChange = (change: TaskBoardChange) =>
        onApply(target.itemIds, change);
    const close = () => onClose(target.card);
    const closeOnHide = (isOpen: boolean) => {
        if (!isOpen) {
            close();
        }
    };
    const trigger = (
        <TaskChipButton
            aria-label={TASK_ACTION_LABELS[field]}
            className={styles.shortcutAnchor}
            style={style}
        />
    );
    if (field === "entities") {
        return createPortal(
            <TaskEntitiesPicker
                entityIds={task?.entityIds ?? []}
                isOpen
                onOpenChange={closeOnHide}
                onChange={onChange}
                trigger={trigger}
            />,
            document.body,
        );
    }
    return createPortal(
        field === "dueDate" ? (
            <DueDateShortcutPicker
                dueDateOnly={task?.dueDateOnly ?? null}
                style={style}
                onChange={onChange}
                onClose={close}
            />
        ) : (
            <TaskFieldPicker
                field={field}
                valueId={
                    task === undefined ? null : getAxisValueIds(task, field)[0]
                }
                isOpen
                onOpenChange={closeOnHide}
                onChange={onChange}
                trigger={trigger}
            />
        ),
        document.body,
    );
};
